import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { AiFillCloseCircle } from "react-icons/ai";
import { FiImage, FiTrash2, FiUploadCloud, FiVideo } from "react-icons/fi";

import { MAX_VIDEO_DURATION } from "../constants";

import { userCoverProfile } from "../api";
import ToastMessage from "./ToastMessage";
import Video from "./Posts/Video";

const ProfileCover = ({ isOpen, isClose, coverType }) => {
  const inputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [videoDuration, setVideoDuration] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isPhoto = coverType === "photo";
  const hasPreview = previewUrls.length > 0;

  const revokeUrls = (urls) => urls.forEach((url) => URL.revokeObjectURL(url));

  const clearPreview = () => {
    revokeUrls(previewUrls);
    setFiles([]);
    setPreviewUrls([]);
    setVideoDuration(0);
    setError("");

    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    return () => revokeUrls(previewUrls);
  }, [previewUrls]);

  const checkVideoDuration = (selectedFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(selectedFile);

      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = video.duration;

        URL.revokeObjectURL(url);

        if (!Number.isFinite(duration) || duration <= 0) {
          reject(new Error("Unable to read the video duration."));
          return;
        }

        if (duration > MAX_VIDEO_DURATION) {
          reject(new Error(`Cover videos can only be ${MAX_VIDEO_DURATION} seconds or less.`));
          return;
        }

        resolve(duration);
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Unable to read this video."));
      };

      video.src = url;
    });
  };

  const handleFile = async (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    setError("");

    try {
      if (isPhoto) {
        if (!selectedFile.type.startsWith("image/")) {
          throw new Error("Please select an image.");
        }

        revokeUrls(previewUrls);

        setFiles([selectedFile]);
        setPreviewUrls([URL.createObjectURL(selectedFile)]);
        setVideoDuration(0);
      } else {
        if (!selectedFile.type.startsWith("video/")) {
          throw new Error("Please select a video.");
        }

        const duration = await checkVideoDuration(selectedFile);

        revokeUrls(previewUrls);

        setFiles([selectedFile]);
        setPreviewUrls([URL.createObjectURL(selectedFile)]);
        setVideoDuration(duration);
      }
    } catch (err) {
      setError(err.message);
      ToastMessage(err.message);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleClose = () => {
    clearPreview();
    isClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files.length) {
      setError(`Please select ${isPhoto ? "an image" : "a video"}.`);
      return;
    }

    try {
      setLoading(true);
      setError("");

      let duration = videoDuration;

      if (!isPhoto) duration = await checkVideoDuration(files[0]);

      const formData = new FormData();

      formData.append("content", files[0]);
      formData.append("type", coverType);

      if (!isPhoto) formData.append("duration", String(duration));

      const res = await userCoverProfile(formData);

      ToastMessage(res.data.message);

      clearPreview();
      isClose();
    } catch (err) {
      console.error("Profile cover update error:", err);

      const message = err?.response?.data?.message || err?.message || "Unable to update profile cover.";

      setError(message);
      ToastMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const preventVideoControlsSubmit = (e) => {
    const button = e.target.closest("button");

    if (!button) return;
    if (button.dataset.profileCoverSubmit === "true") return;

    e.preventDefault();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-out duration-300"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-purple-700 bg-white p-6 text-left shadow-xl transition-all">

                <button type="button" onClick={handleClose} className="absolute right-2 top-2 z-30 rounded-full bg-primary-blue-100 p-2">
                  <AiFillCloseCircle className="h-5 w-5" />
                </button>

                <form onSubmit={handleSubmit} className="mt-7">

                  {!hasPreview && (
                    <div>
                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="flex min-h-[250px] w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-6 transition hover:border-[#6469ff] hover:bg-purple-50/40"
                      >
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 text-[#6469ff]">
                          {isPhoto ? <FiImage size={27} /> : <FiVideo size={27} />}
                        </div>

                        <h3 className="mt-5 text-base font-semibold text-gray-900">
                          Select {isPhoto ? "a cover photo" : "a cover video"}
                        </h3>

                        <p className="mt-2 text-sm text-gray-500">
                          {isPhoto ? "Choose one image from your device" : "Choose one video from your device"}
                        </p>

                        <span className="mt-4 rounded-full bg-purple-100 px-4 py-1.5 text-xs font-semibold text-[#6469ff]">
                          {isPhoto ? "1 image only" : `Maximum ${MAX_VIDEO_DURATION} seconds`}
                        </span>

                        <div className="mt-5 flex items-center gap-2 text-xs font-medium text-gray-400">
                          <FiUploadCloud size={16} />
                          Browse files
                        </div>
                      </button>

                      <input ref={inputRef} type="file" accept={isPhoto ? "image/*" : "video/*"} onChange={handleFile} className="hidden" />
                    </div>
                  )}

                  {hasPreview && (
                    <div>
                      <div className="mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {isPhoto ? "Cover preview" : "Video preview"}
                        </h2>

                        <p className="mt-1 text-sm text-gray-500">
                          Review your new profile cover before updating it.
                        </p>
                      </div>

                      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">

                        {isPhoto ? (
                          <div className="relative h-[300px] w-full overflow-hidden">
                            <img src={previewUrls[0]} alt="Profile cover preview" className="h-full w-full object-cover object-center" />

                            <button
                              type="button"
                              onClick={clearPreview}
                              disabled={loading}
                              className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition hover:bg-red-500 disabled:opacity-50"
                            >
                              <FiTrash2 size={17} />
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <div className="h-[400px] w-full" onClickCapture={preventVideoControlsSubmit}>
                              <Video content={previewUrls[0]} largeScreen={false} />
                            </div>

                            <button
                              type="button"
                              onClick={clearPreview}
                              disabled={loading}
                              className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition hover:bg-red-500 disabled:opacity-50"
                            >
                              <FiTrash2 size={17} />
                            </button>
                          </div>
                        )}
                      </div>

                      {!isPhoto && (
                        <div className="mt-3 flex items-center justify-between">
                          <p className="text-xs font-medium text-gray-400">
                            Maximum video duration: {MAX_VIDEO_DURATION} seconds
                          </p>

                          <p className="text-xs font-semibold text-purple-600">
                            {videoDuration > 0 ? `${videoDuration.toFixed(1)}s` : ""}
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={loading}
                        className="mt-4 w-full rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:border-purple-200 hover:bg-purple-50 hover:text-[#6469ff] disabled:opacity-50"
                      >
                        Choose another {isPhoto ? "photo" : "video"}
                      </button>

                      <input ref={inputRef} type="file" accept={isPhoto ? "image/*" : "video/*"} onChange={handleFile} className="hidden" />
                    </div>
                  )}

                  {error && (
                    <p className="mt-4 text-center text-sm font-medium text-red-500">
                      {error}
                    </p>
                  )}

                  {hasPreview && (
                    <button
                      type="submit"
                      data-profile-cover-submit="true"
                      disabled={!files.length || loading}
                      className="mt-5 w-full rounded-xl bg-[#6469ff] px-10 py-3.5 text-base font-semibold text-white transition hover:bg-[#555be8] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? "Updating..." : "Update cover"}
                    </button>
                  )}
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProfileCover;